
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {LanguageInfo} from './LanguageInfo';
import {FieldModelLanguageInfo} from './LanguageInfo';

/**
 * LanguageInfoListResponse struct
 */
class LanguageInfoListResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!Array=} languages
   * @param {!string=} defaultLanguageCode
   * @constructor
   */
  constructor (parent = new Response(), languages = [], defaultLanguageCode = '') {
    super()
    super.copy(parent)
    this.Languages = languages
    this.DefaultLanguageCode = defaultLanguageCode
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LanguageInfoListResponse}
   * @param {!LanguageInfoListResponse} other Other struct
   * @returns {!LanguageInfoListResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Languages != null) {
      this.Languages = []
      for (let item of other.Languages) {
        if (item != null) {
          let tempItem
          tempItem = LanguageInfo.fromObject(item)
          this.Languages.push(tempItem)
        } else {
          this.Languages.push(null)
        }
      }
    } else {
      this.Languages = null
    }
    if (other.DefaultLanguageCode != null) {
      this.DefaultLanguageCode = other.DefaultLanguageCode
    } else {
      this.DefaultLanguageCode = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LanguageInfoListResponse}
   * @returns {!LanguageInfoListResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LanguageInfoListResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LanguageInfoListResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LanguageInfoListResponse}
   * @param {!LanguageInfoListResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LanguageInfoListResponse)) {
      throw new TypeError('Instance of LanguageInfoListResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LanguageInfoListResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Languages: ((this.Languages != null) ? Array.from(this.Languages, item => ((item != null) ? item : null)) : null),
      DefaultLanguageCode: ((this.DefaultLanguageCode != null) ? this.DefaultLanguageCode : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LanguageInfoListResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LanguageInfoListResponse} other Object value
   * @returns {!LanguageInfoListResponse} Created struct
   */
  static fromObject (other) {
    return new LanguageInfoListResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LanguageInfoListResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LanguageInfoListResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LanguageInfoListResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5561
  }
}

export { LanguageInfoListResponse };

/**
 * Fast Binary Encoding LanguageInfoListResponse field model
 */
class FieldModelLanguageInfoListResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.Languages = new fbe.FieldModelVector(new FieldModelLanguageInfo(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.DefaultLanguageCode = new fbe.FieldModelString(buffer, this.Languages.fbeOffset + this.Languages.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelLanguageInfoListResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelLanguageInfoListResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLanguageInfoListResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Languages.fbeSize + this.DefaultLanguageCode.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLanguageInfoListResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Languages.fbeExtra + this.DefaultLanguageCode.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLanguageInfoListResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLanguageInfoListResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLanguageInfoListResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5561
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLanguageInfoListResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLanguageInfoListResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Languages.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Languages.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Languages.fbeSize

    if ((fbeCurrentSize + this.DefaultLanguageCode.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DefaultLanguageCode.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DefaultLanguageCode.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLanguageInfoListResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLanguageInfoListResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLanguageInfoListResponse}
   * @param {!LanguageInfoListResponse} fbeValue Default value, defaults is new LanguageInfoListResponse()
   * @returns {!LanguageInfoListResponse} LanguageInfoListResponse value
   */
  get (fbeValue = new LanguageInfoListResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLanguageInfoListResponse}
   * @param {!LanguageInfoListResponse} fbeValue LanguageInfoListResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Languages.fbeSize) <= fbeStructSize) {
      this.Languages.get(fbeValue.Languages)
    } else {
      fbeValue.Languages.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Languages.fbeSize

    if ((fbeCurrentSize + this.DefaultLanguageCode.fbeSize) <= fbeStructSize) {
      fbeValue.DefaultLanguageCode = this.DefaultLanguageCode.get()
    } else {
      fbeValue.DefaultLanguageCode = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DefaultLanguageCode.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLanguageInfoListResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLanguageInfoListResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLanguageInfoListResponse}
   * @param {!LanguageInfoListResponse} fbeValue LanguageInfoListResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLanguageInfoListResponse}
   * @param {LanguageInfoListResponse} fbeValue LanguageInfoListResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Languages.set(fbeValue.Languages)
    this.DefaultLanguageCode.set(fbeValue.DefaultLanguageCode)
  }
}

export { FieldModelLanguageInfoListResponse };

/**
 * Fast Binary Encoding LanguageInfoListResponse model
 */
class LanguageInfoListResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLanguageInfoListResponse(this.buffer, 4)
  }

  /**
   * Get the LanguageInfoListResponse model
   * @this {!LanguageInfoListResponseModel}
   * @returns {!FieldModelLanguageInfoListResponse} model LanguageInfoListResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LanguageInfoListResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LanguageInfoListResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LanguageInfoListResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LanguageInfoListResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLanguageInfoListResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LanguageInfoListResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LanguageInfoListResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LanguageInfoListResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LanguageInfoListResponseModel}
   * @param {!LanguageInfoListResponse} value LanguageInfoListResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LanguageInfoListResponseModel}
   * @param {!LanguageInfoListResponse} value LanguageInfoListResponse value, defaults is new LanguageInfoListResponse()
   * @return {!object} Deserialized LanguageInfoListResponse value and its size
   */
  deserialize (value = new LanguageInfoListResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LanguageInfoListResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LanguageInfoListResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LanguageInfoListResponseModel}
   * @param {!number} prev Previous LanguageInfoListResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LanguageInfoListResponseModel };
